import { GetStaticPaths } from 'next';
import { TFunction, useTranslation } from 'next-i18next';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo, ImageJsonLd } from 'next-seo';
import sanitize from 'sanitize-html';
import isEmpty from 'lodash/isEmpty';
import { TLocale } from '@typescript/index';
import { IParams } from '@features/categories';
import { getLayoutData } from '@features/layout/api';
import { REVALIDATION_TIME, PUBLIC_HOST } from '@config/index';
import ShowroomSection from '@features/showrooms/sections/ShowroomSection/ShowroomSection';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import nextShowroomApi from '@features/api/nextApiService/nextShowroomApi';
import { IPointOfPresence, IPointOfPresenceWithDetails } from '@features/checkout/typescript';
import { getShowroomType, prepareShowroomUrl } from '@utils/showroom';
import { DEFAULT_LOCALE, SHOWROOMS_SEO_DESCRIPTION_LENGTH } from '@const/index';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
type PropsType = {
  showroom: IPointOfPresenceWithDetails;
  showPartnersLogos: boolean;
  isBuildTime?: boolean;
};
const Showroom = ({
  showroom,
  showPartnersLogos,
  isBuildTime
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  if (isBuildTime) {
    return <div>Build time</div>;
  }
  const seoDescription = () => {
    if (!showroom?.description?.summary) {
      return undefined;
    }
    const CONFIG_SANITIZE = {
      allowedTags: ['br'],
      allowedAttributes: {}
    };
    const sanitizeDescription = sanitize(showroom?.description?.summary || '', CONFIG_SANITIZE);
    return sanitizeDescription.length > SHOWROOMS_SEO_DESCRIPTION_LENGTH ? `${sanitizeDescription.substring(0, SHOWROOMS_SEO_DESCRIPTION_LENGTH - 3)}...` : sanitizeDescription;
  };
  const getImagesJsonLd = (): ImageJsonLd[] => {
    if (!showroom?.gallery || showroom.gallery.length === 0) return [];
    return showroom.gallery.map(item => ({
      contentUrl: item.sizes.ORGINAL,
      creator: {
        '@type': 'Organisation',
        name: 'VOX'
      },
      copyrightNotice: '© VOX',
      creditText: PUBLIC_HOST
    }));
  };
  return <>
      <SelfCanonicalLink />
      <main>
        {showroom && showroom?.address && <>
            <NextSeo title={`${getShowroomType(showroom?.group?.code, t)}. ${showroom.address.city}, ${showroom.address.street}`} description={seoDescription()} />
            {showroom?.gallery && showroom?.gallery.length > 0 && <ImageJsonLd images={getImagesJsonLd()} />}
          </>}
        <ShowroomSection showroom={showroom} showPartnersLogos={showPartnersLogos} />
      </main>
    </>;
};
export const getStaticProps = async ({
  params,
  locale
}: GetStaticPropsContext<IParams>) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        isBuildTime: true
      },
      revalidate: 1
    };
  }
  if (params === undefined) {
    return new Error('Params missing');
  }
  const pimInternalId = params?.pimInternalId as string;
  const showPartnersLogos = process.env?.SHOW_PARTNERS_LOGOS === 'true' || false;
  if (!pimInternalId) return {
    notFound: true,
    revalidate: true
  };
  let showroom = {} as IPointOfPresence;
  try {
    showroom = await nextShowroomApi.getShowroomByPimInternalId(pimInternalId);
    if (!showroom || isEmpty(showroom)) {
      console.debug(`[${pimInternalId}] ShowroomsPage returning 404 - no showroom found`);
      return {
        notFound: true,
        revalidate: true
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.debug(`[${pimInternalId}] ShowroomsPage returning error - error found `, {
      error
    });
    throw new Error(error);
  }
  const translations = await serverSideTranslations(locale as string, ['common']);
  const transArray = locale && translations._nextI18Next?.initialI18nStore[locale]?.common ? translations._nextI18Next.initialI18nStore[locale].common : {};
  const translate = (key: string): string | undefined => {
    const split = key.split('.');
    let current = transArray;
    split.forEach(value => {
      current = current[value];
    });
    if (current) {
      return current;
    }
    return key;
  };
  const urlName = params.name as string;
  const properProductSlug = prepareShowroomUrl(showroom?.group?.code, showroom?.name, pimInternalId, translate as TFunction);
  const subString = properProductSlug.substring(properProductSlug.indexOf('showrooms/') + 'showrooms/'.length, properProductSlug.lastIndexOf('/'));
  if (urlName !== subString) {
    return {
      redirect: {
        destination: locale && locale !== DEFAULT_LOCALE ? `/${locale}/${properProductSlug}` : properProductSlug,
        permanent: true
      },
      revalidate: false
    };
  }
  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      showroom,
      showPartnersLogos,
      isBuildTime: false,
      ...(await getLayoutData(locale as TLocale)),
      ...(await serverSideTranslations(locale as string))
    },
    revalidate: REVALIDATION_TIME
  };
};
export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});
export default Showroom;